import React, { useState, useEffect, useMemo } from "react";
import * as yup from "yup";
import Swal from "sweetalert2";
import DynamicForm from "../../../../data/Axios/DynamicForm";
import { AccountsandFinanceurl } from "../../../../config";
import {
  generateAndExecuteMutation,
  graphqlQuery,
} from "../../../../data/Axios/DynamicService";
import { allAccountCategories } from "../../../../data/Axios/queries";

const base_url = AccountsandFinanceurl.uri;

const validationSchema = yup.object({
  accountName: yup.string().trim().required("Account sub-category name is required"),
  accountCategoryId: yup
    .number()
    .typeError("Select an account category")
    .integer("Select a valid account category")
    .positive("Select an account category")
    .required("Account category is required"),
});

const AccountSubCategoryForm = (props) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const companyId = parseInt(decodedToken.CompanyId);
  const [categoryData, setCategoryData] = useState([]);

  useEffect(() => {
    async function fetchAllData() {
      try {
        const data = await graphqlQuery(allAccountCategories, base_url);
        setCategoryData(data || []);
      } catch (error) {
        console.error("Error fetching account categories:", error);
      }
    }
    fetchAllData();
  }, []);

  const CategoryOptions = useMemo(
    () =>
      categoryData.map((data) => ({
        parent_key: data.id,
        value: data.id,
        label: data.categoryName,
      })),
    [categoryData]
  );

  const fields = useMemo(
    () => [
      {
        name: "accountName",
        label: "Account Sub-Category Name *",
        type: "text",
        gridColumn: "span 2",
        required: true,
      },
      {
        name: "accountCategoryId",
        label: "Account Category *",
        type: "select",
        options: CategoryOptions,
        gridColumn: "span 2",
        required: true,
      },
    ],
    [CategoryOptions]
  );

  const initialValues = {
    id: props.data?.id ?? "",
    accountName: props.data?.accountCategoryName || props.data?.accountName || "",
    accountCategoryId: props.data?.accountCategoryId ?? null,
    companyId: props.data?.companyId ?? companyId,
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const { accountCategoryId, accountName } = values;
      const categoryId = Number(accountCategoryId);

      const selectedCategory = CategoryOptions.find(
        (c) => c.value === categoryId
      )?.label;

      if (!Number.isInteger(categoryId) || categoryId <= 0 || !selectedCategory) {
        throw new Error("Select a valid account category before saving.");
      }

      const mutationData = props.isEditing
        ? {
            id: props.data.id,
            accountCategoryId: categoryId,
            accountCategoryName: accountName.trim(),
            accountCategory: selectedCategory,
            companyId,
            isActive: true,
          }
        : {
            accountCategoryId: categoryId,
            accountCategoryName: accountName.trim(),
            accountCategory: selectedCategory,
            companyId,
            isActive: true,
          };

      const response = await generateAndExecuteMutation(
        props.isEditing
          ? "updateAccountSubCategory"
          : "createAccountSubCategory",
        mutationData,
        props.isEditing ? "updatedAccountSubCategory" : "newAccountSubCategory",
        base_url
      );

      if (Object.values(response)[0] != null) {
        props.onClose();
        props.onAction();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Account sub-category ${
            props.isEditing ? "updated" : "created"
          } successfully.`,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: error.message || "Unable to save the account sub-category. Please try again.",
      });
    }

    setSubmitting(false);
  };

  return (
    <DynamicForm
      initialValues={initialValues}
      fields={fields}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      title={
        props.isEditing
          ? "Edit Account Sub-Category"
          : "Create Account Sub-Category"
      }
      subtitle={
        props.isEditing
          ? "Edit an Existing Account Sub-Category"
          : "Create a New Account Sub-Category"
      }
    />
  );
};

export default AccountSubCategoryForm;
